/**
 * Runtime Converter Utils
 * Pure functions for converting between graph/runtime formats.
 * 
 * @module utils/compiler/runtimeConverter
 */

/**
 * Convert Visual Editor graph to runtime story format
 * @param {Object} graph - Graph with nodes and edges
 * @param {string} tweeCode - Twee code for title extraction
 * @returns {Object} Runtime story format
 */
export const convertGraphToRuntimeStory = (graph, tweeCode = '') => {
  if (!graph || !Array.isArray(graph.nodes)) {
    return { id: 'empty', title: 'Empty Story', startNodeId: 'Start', nodes: [], connections: [] };
  }

  // Extract title from twee code
  const titleMatch = tweeCode?.match(/:: StoryTitle\s*\n\s*(.+)/);
  const storyTitle = titleMatch ? titleMatch[1].trim() : 'PathNav Story';

  // Build edges map
  const edgesMap = new Map();
  (graph.edges || []).forEach(edge => {
    const from = edge.from || edge.source;
    const to = edge.to || edge.target;
    if (from && to) {
      if (!edgesMap.has(from)) edgesMap.set(from, []);
      edgesMap.get(from).push(to);
    }
  });

  // Convert nodes
  const nodes = graph.nodes.map(node => {
    const children = edgesMap.get(node.id) || [];
    const bodyText = node.body || '';
    const cleanBody = bodyText
      .replace(/\[\[([^|\]]+)\|[^\]]+\]\]/g, '$1')
      .replace(/\[\[([^\]]+)\]\]/g, '$1')
      .trim();

    return {
      id: node.id,
      title: node.displayName || node.name || node.id,
      body: cleanBody,
      url: node.url || null,
      nodeStyle: node.nodeStyle || 'standard',
      choices: children.map(childId => {
        const childNode = graph.nodes.find(n => n.id === childId);
        return {
          label: childNode?.displayName || childNode?.name || childId,
          nextNodeId: childId
        };
      })
    };
  });

  // Find start node
  const startNodeId = graph.nodes.find(n => n.name === 'Home' || n.name === 'Start')?.id
    || graph.nodes[0]?.id
    || 'Start';

  return {
    id: 'visual-editor-story',
    title: storyTitle,
    startNodeId,
    nodes,
    connections: graph.edges || []
  };
};

/**
 * Convert runtime story to Twee-like format for display
 * @param {Object} runtimeStory - Runtime story object
 * @returns {string} Twee formatted text
 */
export const convertRuntimeToTweeFormat = (runtimeStory) => {
  if (!runtimeStory || !Array.isArray(runtimeStory.nodes)) {
    return ':: StoryTitle\nEmpty Story\n';
  }

  let tweeText = `:: StoryTitle\n${runtimeStory.title}\n\n`;
  tweeText += `:: StoryData\n{\n  "format": "TweePorter Runtime",\n  "tracking": true,\n  "nodes": ${runtimeStory.nodes.length}\n}\n\n`;

  runtimeStory.nodes.forEach(node => {
    tweeText += `:: ${node.id}\n`;
    tweeText += `${node.title}\n\n`;
    const bodyText = node.body.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ');
    tweeText += `${bodyText}\n\n`;

    if (node.choices?.length > 0) {
      node.choices.forEach(choice => {
        tweeText += `[[${choice.label}|${choice.nextNodeId}]]\n`;
      });
    } else {
      tweeText += `[End of Story]\n`;
    }
    tweeText += `\n`;
  });

  tweeText += `\n---\n⚠️ NOTE: This is a tracking-enabled story using the Runtime Engine.\n`;
  tweeText += `The code above is a Twee representation for reference only.\n`;
  tweeText += `Click "Compile Story" to generate the tracking-enabled HTML.\n`;

  return tweeText;
};
